import { parseImportResults, sendToBusinessCentral } from "../utils/businessCentralUtils";
import { formatAxiosError } from "../utils/common";
import { anotarFalloDeLote } from "./syncLog";

/**
 * Piezas compartidas del envío por lote a Business Central.
 *
 * Cada maestro mandaba sus pendientes de a uno: con veinte registros eran veinte
 * viajes de ida y vuelta. Las acciones `Importaciones_*` siempre recibieron un
 * array y lo recorren con un `foreach`; era el cliente el que mandaba uno solo
 * por llamada. Del lado de BC cada registro se procesa aislado en una try
 * function, así que un rechazo no arrastra a los demás y la respuesta trae un
 * resultado por registro, en el mismo orden.
 *
 * Esto vive aparte porque el bloque es idéntico en los seis maestros que pueden
 * ir por lote, y la parte delicada -- qué hacer cuando la respuesta no se puede
 * repartir -- conviene tenerla escrita una sola vez.
 */

// Un guardado correcto limpia el syncError del registro, y con él desaparece el
// único rastro de que Business Central había rechazado una baja. El estado que
// queda es cierto -- el registro existe en los dos lados y coincide -- pero por
// sí solo da a entender que el asunto se resolvió, y no es así: la baja nunca
// llegó a aplicarse y sigue haciendo falta resolver el motivo antes de volver a
// intentarla.
export const AVISO_BAJA_NO_APLICADA = "La baja anterior no llegó a aplicarse en Business Central.";

/**
 * Agrega el aviso al mensaje cuando el registro traía una baja rechazada.
 * El estado anterior hay que leerlo ANTES del update, que es justo lo que lo borra.
 */
export const conAvisoDeBajaRechazada = (mensaje, teniaSyncError) =>
  teniaSyncError ? `${mensaje} ${AVISO_BAJA_NO_APLICADA}`.trim() : mensaje;

/**
 * Resumen del lote con la misma forma que devuelve BC (Total / Correctos /
 * Errores), calculado sobre lo que de verdad se aplicó en local.
 *
 * @param {Array<{ok: boolean}>} detalle - Una fila por registro enviado.
 */
export const resumenDelLote = (detalle) => ({
  total: detalle.length,
  correctos: detalle.filter((fila) => fila.ok).length,
  errores: detalle.filter((fila) => !fila.ok).length,
  filas: detalle,
});

/**
 * Manda un lote a una acción de importación y devuelve un resultado por
 * registro, alineado por posición con lo que se envió.
 *
 * Si la llamada entera falla -- red, autenticación, o un error que BC no
 * capturó -- no hay resultados que repartir, así que fallan todos con el mismo
 * motivo. Es la lectura segura: dar por buenos algunos a ciegas dejaría la base
 * local afirmando cambios que BC nunca aplicó. BC rechaza con HTTP 400 y el
 * motivo real dentro de `error.response.data.error.message`, que es lo que
 * rescata `formatAxiosError`.
 *
 * @param {string} accion - Nombre de la acción, ej. "Importaciones_insertaColegios".
 * @param {object} payload - Lo que devuelve el transform del maestro.
 * @param {number} cantidad - Cuántos registros lleva el lote.
 * @param {string} etiqueta - Nombre del maestro para el mensaje de consola.
 */
export const enviarLote = async (accion, payload, cantidad, etiqueta) => {
  try {
    const response = await sendToBusinessCentral(accion, payload, true);
    return parseImportResults(response, cantidad);
  } catch (error) {
    const motivo = formatAxiosError(error, "No se pudo completar el envío a Business Central");

    // La única línea roja del fallo: los registros que quedan sin aplicar se
    // anotan después como avisos, en vez de repetir el mismo error N veces.
    anotarFalloDeLote(etiqueta, motivo, cantidad);

    return {
      ok: false,
      total: 0,
      correctos: 0,
      errores: cantidad,
      results: Array.from({ length: cantidad }, () => ({
        ok: false,
        assignedNo: null,
        action: "error",
        message: motivo,
      })),
    };
  }
};

/**
 * Separa los pendientes en los que hay que mandar a BC y los que sólo existen en
 * local.
 *
 * Un registro marcado como eliminado y sin `No` nunca llegó a BC (se creó y se
 * borró sin sincronizar en el medio): se borra y no hay nada que avisarle a BC.
 * Se resuelven antes de armar el lote para no gastar un hueco del envío en algo
 * que no necesita viajar.
 */
export const separarPendientes = (pendientes) => ({
  soloLocales: pendientes.filter((registro) => registro.deleted && !registro.No),
  aEnviar: pendientes.filter((registro) => !(registro.deleted && !registro.No)),
});

/**
 * Motivo a reportar cuando un guardado no dejó al registro utilizable.
 * Un alta que BC da por buena pero sin devolver número no cuenta como correcta:
 * el registro se queda pendiente y el cuadro tiene que decirlo.
 */
export const motivoDeGuardadoFallido = (resultado) =>
  resultado.message ||
  (resultado.ok
    ? "Business Central no devolvió el número asignado"
    : "Respuesta inválida de Business Central");
